import { useEffect, useRef, useState } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { ApiError, api } from '../lib/api';
import { Card } from '../components/ui';

/**
 * Where the provider's round trip ends.
 *
 * The API put a one-time code in this URL rather than the tokens themselves, because a URL
 * reaches browser history, the referrer of whatever loads next, and every proxy log in
 * between. This page spends the code on a POST and puts what comes back where a password
 * sign-in would have put it.
 */
export function SsoComplete() {
  const [params] = useSearchParams();
  const { signedIn, adopt } = useAuth();
  const navigate = useNavigate();

  const [error, setError] = useState<string | null>(null);

  const code = params.get('code');

  // The API spends the code on first use, so a second attempt fails. React runs effects
  // twice in development, and without this guard the second run is what the person sees.
  const spent = useRef(false);

  useEffect(() => {
    if (!code || spent.current) return;
    spent.current = true;

    let cancelled = false;

    api.auth.ssoExchange({ code })
      .then((session) => {
        if (cancelled) return;
        adopt(session);

        // Only ever a path: the API already refused anything with a scheme or a host
        // before it put this in the URL, and this repeats the check rather than trusting
        // that it happened.
        const next = params.get('next');
        const safe = next && next.startsWith('/') && !next.startsWith('//') ? next : '/';

        navigate(safe, { replace: true });
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof ApiError
          ? err.message
          : 'That sign-in could not be completed. Try again.');
      });

    return () => { cancelled = true; };
  }, [code, adopt, navigate, params]);

  if (signedIn && !error) return <Navigate to="/" replace />;

  if (!code) return <Navigate to="/login?sso=failed" replace />;

  return (
    <div className="grid min-h-full place-items-center p-6">
      <Card className="w-full max-w-sm p-6 text-center">
        {error ? (
          <>
            <p role="alert" className="text-sm text-[var(--ff-danger)]">{error}</p>
            <button
              type="button"
              className="mt-4 text-sm text-[var(--ff-primary)] underline"
              onClick={() => navigate('/login', { replace: true })}
            >
              Back to sign in
            </button>
          </>
        ) : (
          <p className="text-sm text-[var(--ff-text-muted)]">Signing you in…</p>
        )}
      </Card>
    </div>
  );
}
